import { toJS } from 'mobx';

import { set } from 'lodash';

import { LessonMode, QuestionFeedbackState, questionFeedbackFromScores } from '../../Constants';

import questionService from '../services/QuestionService';
import UtilsService from '../services/UtilsService';
import lessonManager from '../managers/LessonManager';
import responseManager from '../managers/ResponseManager';

import { Behavior } from './Behavior';

export class RelationshipsBehavior extends Behavior {
  checkForValidResponse = (responseAnswer, _model) => {
    const nestedPrompts = toJS(responseAnswer.prompts || []);
    const flatPrompts = UtilsService.flattenChildren(nestedPrompts);

    const hasSomeEmptyResponses = flatPrompts?.some?.((prompt) => {
      return !prompt?.content && !prompt?.sample;
    });
    return !hasSomeEmptyResponses;
  }

  isAutoScored = (_model) => {
    return false;
  }

  setResponseData = ({ index, pathId, prompt, text }, responseAnswer, _model) => {
    if (index >= 0) {
      const responseAnswerPrompts = toJS(responseAnswer.prompts || {});

      const updatedPrompt = {
        ...prompt,
        content: text
      };

      set(responseAnswerPrompts, pathId, updatedPrompt);

      responseAnswer.prompts = responseAnswerPrompts;
    }
  }

  getAnswerFeedback = (_id, _responseAnswer) => {
    return true;
  }

  getInitializedResponse = (lessonElementId, model, _activityId) => {
    const prompts = toJS(model.tree || []);

    const responseAnswer = {
      ...questionService.generateInitializedQuestionResponse({
        isScored: false,
        isScoredCorrect: false,
        lessonElementId,
        prompts
      })
    };
    return responseAnswer;
  }

  getCorrectAnswersText = (_model) => {
    return '';
  }

  // eslint-disable-next-line no-empty-pattern
  getQuestionFeedbackText = (_model, _correctFeedback,
    _incorrectFeedback, _correctFeedbackBody, _incorrectFeedbackBody,
    mode, _questionIsCorrect, currentAttemptCount, { }) => {
    let textBody = '';
    if (mode === LessonMode.PREVIEW || mode === LessonMode.STUDENT_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
      textBody += UtilsService.previewWarning();
    } else {
      if (typeof currentAttemptCount !== 'undefined' && currentAttemptCount !== -1 && currentAttemptCount > 1) {
        textBody += UtilsService.saveWarning();
      } else {
        textBody += UtilsService.saveCommit();
      }
    }

    const textHeader = '';

    let finalText = '<div class=\'question-feedback-header\'>';
    finalText += textHeader;
    finalText += '</div>';
    finalText += '<div class=\'question-feedback-body\'>';
    finalText += textBody;
    finalText += '</div>';

    return finalText;
  }

  isAnswerCorrect = (_answerId, _lessonElementState, _model) => {
    return false;
  }

  isQuestionCorrect = (lessonElementState, _lessonElementId) => {
    if (!lessonElementState) {
      return QuestionFeedbackState.INCORRECT;
    }
    return questionFeedbackFromScores(lessonElementState.submittedItemScore, lessonElementState.maxScore);
  }

  // Response dependency: copy the previous question's typed answers (`content`) onto this
  // question's tree. Nodes have no ids, so match by tree position first, then hand any
  // unmatched source answers to the remaining empty answer cells in reading order.
  // Label cells (non-empty `sample`) are authored content and are never overwritten.
  applyPreviousResponseContent = (lessonElementId, content) => {
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    const model = lessonManager.getSlideModel(lessonElementId);
    if (!lessonElementState || !content) {
      return;
    }

    const hasText = (value) => UtilsService.stripTagsAndEntities(value || '').trim() !== '';
    // Same rule the Relationships component uses to decide which cells are editable.
    const isAnswerCell = (node) => !!node && !node.sample;

    const targetTree = toJS(lessonElementState.currentResponseAnswer?.prompts || model?.tree || []);
    const sourceTree = toJS(content.prompts || []);
    const consumedSources = new Set();

    const walk = (targetNodes, sourceNodes) => (targetNodes || []).map((targetNode, index) => {
      const sourceNode = sourceNodes?.[index];
      const next = { ...targetNode };
      if (isAnswerCell(targetNode)) {
        next.content = '';
        if (isAnswerCell(sourceNode) && hasText(sourceNode.content)) {
          next.content = sourceNode.content;
          consumedSources.add(sourceNode);
        }
      }
      next.children = walk(targetNode.children, sourceNode?.children);
      return next;
    });
    const prompts = walk(targetTree, sourceTree);

    const leftoverSources = (UtilsService.flattenChildren(sourceTree) || [])
      .filter((node) => isAnswerCell(node) && hasText(node.content) && !consumedSources.has(node));
    const fillRemaining = (nodes) => (nodes || []).forEach((node) => {
      if (leftoverSources.length && isAnswerCell(node) && !hasText(node.content)) {
        node.content = leftoverSources.shift().content;
      }
      fillRemaining(node.children);
    });
    fillRemaining(prompts);

    const cloned = UtilsService.safeMobxClone({
      ...content,
      lessonElementId,
      prompts,
      responseDependencyAppliedAt: Date.now()
    });
    lessonElementState.setCurrentResponse(cloned);
  }
}

export default new RelationshipsBehavior();
